
import { fontText, hexToRgbA, StylesIndexInjections } from 'common-react';
import { fieldBoxShadow, flexCenter, focusStyle, scrollbarStyleMixin } from '../../styles/mixins';
import { makeUiStyles, Theme, createStyles } from '../../styles/styles';
import { DropdownSize } from './tools/Dropdown.const';

export interface CustomDropdownStyleProps {
  isDarkTheme?: boolean;
  value: string | string[];
  placeholder?: string;
  multiple?: boolean;
  variant?: 'default' | 'compact';
  size?: DropdownSize;
}

const getPlaceholderColor = (theme: Theme, value: string | string[], isDarkTheme = false, multiple = false) => {
  if (isDarkTheme && multiple) {
    return value.length > 0 ? theme.color.greys[80] : theme.color.greys[20]
  }
  if (!multiple) {
    return isDarkTheme ? theme.color.greys[20] : theme.color.greys[50]
  }
  return value.length > 0 ? theme.color.greys[80] : theme.color.greys[50]
}

const dropdownSize = (size?: DropdownSize) => {
  if (size === DropdownSize.SMALL) {
    return {
      padding: '10px 32px 10px 10px',
      height: 40,
    }
  }
  return {
    padding: '16px 48px 16px 16px',
    height: 56,
  }
}

export default makeUiStyles((theme: Theme) => createStyles({
  formControl: {
    width: '100%',
    '& .MuiInput-formControl': {
      margin: 0,
    },
    '& .MuiFormLabel-root': {
      marginBottom: 8
    }
  },
  menuItemLabelWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 4,
  },
  menuItemLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  root: {
    color: ({ value, isDarkTheme, multiple }: CustomDropdownStyleProps) => getPlaceholderColor(theme, value, isDarkTheme, multiple),
    font: fontText('normal', theme.fontSize.base, 24),
    border: `1px solid ${theme.color.greys[40]}`,
    height: props => dropdownSize(props.size).height,
    backgroundColor: theme.color.greys[0],
    borderRadius: 8,
    padding: 0,
    [theme.breakpoints.down('xs')]: {
      overflow: 'hidden',
      width: '100%',
      display: 'flex',
      justifyContent: 'center'
    },
    '&.Mui-error': {
      borderColor: theme.color.reds[100],
    },
    '&.Mui-focused': {
      boxShadow: fieldBoxShadow(theme).bs1,
      borderColor: theme.color.greys[80],
      caretColor: theme.color.oranges[50],
      backgroundColor: 'transparent',
      color: ({ value, isDarkTheme, multiple }: CustomDropdownStyleProps) => getPlaceholderColor(theme, value, isDarkTheme, multiple),
    },
    '&.Mui-focusVisible': {
      ...focusStyle(theme),
      boxShadow: 'none',
      borderColor: theme.color.greys[40]
    },
    '&:hover': {
      boxShadow: fieldBoxShadow(theme).bs1,
      outline: 'none',
      borderColor: theme.color.greys[80],
    },
    '&.Mui-disabled': {
      border: `1px solid ${hexToRgbA(theme.color.greys[40], 0.4)}`,
      outline: 'none',
      boxShadow: 'none',
      color: hexToRgbA(theme.color.greys[40], 0.4),
      backgroundColor: theme.color.greys[0],
      pointerEvents: 'none',
      '& $selected, & $placeholder': {
        color: 'inherit',
      },
    },
    '& .MuiSelect-root': {
      padding: props => dropdownSize(props.size).padding,
      borderRadius: 8,
      backgroundColor: 'transparent',
      '&:focus': {
        backgroundColor: 'transparent',
      }
    },
    '& .MuiSelect-icon': {
      '&.Mui-disabled': {
        color: hexToRgbA(theme.color.oranges[50], 0.4),
      }
    },
    '& $select': {
      minWidth: 140,
      font: fontText('normal', theme.fontSize.base, 24),
      [theme.breakpoints.down('xs')]: {
        minWidth: 'auto',
      },
      '&:focus': {
        backgroundColor: theme.color.greys[0],
      },
    },
  },
  icon: {
    position: 'absolute',
    right: 16,
    transform: 'rotate(0deg)',
    top: 'calc(50% - 10px)',
    width: 20,
    height: 20,
    color: theme.color.greys[80],
    pointerEvents: 'none',
    willChange: 'transform',
    transition: 'transform 0.2s ease-in-out',
    '&.MuiSelect-iconOpen': {
      transform: 'rotate(180deg)',
      color: theme.color.greys[80],
    },
  },
  menuItem: {
    color: theme.color.greys[80],
    font: fontText('normal', theme.fontSize.base, 24),
    whiteSpace: 'unset',
    padding: '16px 16px',
    borderRadius: 8,
    '&:hover': {
      backgroundColor: theme.color.greys[10],
    },
    '&.Mui-selected': {
      boxShadow: fieldBoxShadow(theme).bs2,
      backgroundColor: theme.color.greys[0],
      '&:hover': {
        backgroundColor: theme.color.greys[0],
      },
    },
    '&.Mui-focusVisible:not(.Mui-selected)': {
      boxShadow: fieldBoxShadow(theme).bs2,
      backgroundColor: theme.color.greys[0],
    }
  },
  placeholder: {
    color: theme.color.greys[80],
    font: fontText('normal', theme.fontSize.base, 24),
  },
  selected: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: theme.color.greys[80],
    font: fontText('normal', theme.fontSize.base, 24),
  },
  popover: {
    backgroundColor: theme.color.greys[0],
    maxHeight: 418,
    boxShadow: fieldBoxShadow(theme).bs3,
    border: '1px solid transparent',
    borderRadius: 8,
    marginTop: 8,
    overflowY: 'auto',
    width: 'auto',
    paddingTop: 16,
    ...scrollbarStyleMixin({ theme }),
    '& .MuiList-padding': {
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }
  },
  menuItemMulti: {

    '&:hover': {
      backgroundColor: theme.color.greys[20],
    },
    '&:focus-visible': {
      boxShadow: fieldBoxShadow(theme).bs2,
    },
    '&.Mui-selected': {
      boxShadow: fieldBoxShadow(theme).bs2,
      backgroundColor: theme.color.greys[0],
    },
  },
  menuItemTextMulti: {
    marginRight: 20,
    color: theme.color.greys[80],
    font: fontText('normal', theme.fontSize.s, 20),
    whiteSpace: 'break-spaces',
    minWidth: 140
  },
  actionButtonBox: {
    ...flexCenter(),
    position: 'sticky',
    padding: '16px 12px',
    bottom: 0,
    borderTop: `1px solid ${theme.color.greys[15]}`,
    backgroundColor: theme.color.greys[0],
  },
  numberOfOptions: {
    color: theme.color.violets[50],
  },
  menuItemLabelDescription: {
    color: theme.color.greys[40],
    font: fontText('normal', theme.fontSize.s, 18),
    flexBasis: '100%',
  }
}), { name: 'Dropdown', index: StylesIndexInjections.UI_MOLECULES });
